import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { formatDateBR, formatDateTimeBR } from "../utils/dateUtils";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Music,
  Heart,
  Brain,
  GraduationCap,
  Dumbbell,
  Search,
  ChevronRight,
  Calendar,
  MapPin,
  Clock,
  User,
  Target,
  Loader2,
  ClipboardList,
  UserPlus,
  FileText,
  CheckCircle,
  HandHeart,
  Baby,
  Shirt,
  Package,
  Apple,
  Cross,
  Check,
  X,
  History,
  CheckCheck,
  CreditCard,
  Copy,
  DollarSign,
  Users2,
  Upload,
  Image,
  Link as LinkIcon,
  Settings,
  BookOpen,
  Save,
  MessageCircle,
  CalendarDays
} from "lucide-react";
import ProjectSettingsModal from "../components/ProjectSettingsModal";
import ProjectAgendaTab, { nextAppointmentByPessoa, appointmentMessage } from "../components/ProjectAgendaTab";
import StudentAreaModal from "../components/StudentAreaModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { value: "evento", label: "Evento", icon: Calendar, color: "bg-amber-500" },
  { value: "evento_dupla", label: "Evento em Dupla", icon: Users2, color: "bg-pink-500" },
  { value: "inscricao", label: "Inscrição Geral", icon: ClipboardList, color: "bg-teal-500" },
  { value: "musica", label: "Música", icon: Music, color: "bg-purple-500" },
  { value: "doacoes", label: "Doações", icon: Heart, color: "bg-red-500" },
  { value: "terapias", label: "Terapias", icon: Brain, color: "bg-blue-500" },
  { value: "cursos", label: "Cursos", icon: GraduationCap, color: "bg-green-500" },
  { value: "esporte", label: "Esporte", icon: Dumbbell, color: "bg-orange-500" },
  { value: "oracao", label: "Oração", icon: HandHeart, color: "bg-indigo-500" },
  { value: "evento_infantil", label: "Evento Infantil", icon: Baby, color: "bg-yellow-500" },
];

const PROJECT_ICONS = [
  { value: "default", label: "Padrão (por tipo)", icon: null },
  { value: "clipboard", label: "Inscrição/Lista", icon: ClipboardList },
  { value: "calendar", label: "Evento/Agenda", icon: Calendar },
  { value: "users2", label: "Evento em Dupla", icon: Users2 },
  { value: "user-plus", label: "Cadastro", icon: UserPlus },
  { value: "music", label: "Música", icon: Music },
  { value: "heart", label: "Doação Geral", icon: Heart },
  { value: "hand-heart", label: "Ajuda/Oração", icon: HandHeart },
  { value: "shirt", label: "Doação de Roupa", icon: Shirt },
  { value: "package", label: "Doação de Cesta", icon: Package },
  { value: "apple", label: "Doação de Alimentos", icon: Apple },
  { value: "brain", label: "Terapia/Mente", icon: Brain },
  { value: "graduation", label: "Curso/Educação", icon: GraduationCap },
  { value: "dumbbell", label: "Esporte", icon: Dumbbell },
  { value: "cross", label: "Oração/Fé", icon: Cross },
];

const ORACAO_AREAS = [
  { value: "financeira", label: "Área Financeira" },
  { value: "familiar", label: "Área Familiar" },
  { value: "conjugal", label: "Área Conjugal" },
  { value: "relacionamentos", label: "Relacionamentos" },
  { value: "vida_espiritual", label: "Vida Espiritual" },
  { value: "emocional", label: "Área Emocional" },
  { value: "saude", label: "Saúde" },
  { value: "profissional", label: "Área Profissional" },
  { value: "outros", label: "Outros" },
];

const STATUS_OPTIONS = [
  { value: "ativo", label: "Ativo", color: "bg-green-500" },
  { value: "em_planejamento", label: "Em Planejamento", color: "bg-yellow-500" },
  { value: "encerrado", label: "Encerrado", color: "bg-gray-500" },
];

const PARTICIPANT_STATUS = [
  { value: "ativo", label: "Ativo", icon: "🟢", color: "text-green-600 bg-green-100", isActive: true },
  { value: "liberado", label: "Liberado", icon: "✅", color: "text-blue-600 bg-blue-100", isActive: true },
  { value: "aprovado", label: "Aprovado", icon: "👍", color: "text-emerald-600 bg-emerald-100", isActive: true },
  { value: "encaminhado", label: "Encaminhado", icon: "➡️", color: "text-amber-600 bg-amber-100", isActive: false },
  { value: "atendido", label: "Atendido", icon: "✔️", color: "text-purple-600 bg-purple-100", isActive: false },
  { value: "inativo", label: "Inativo", icon: "⏸️", color: "text-gray-600 bg-gray-100", isActive: false },
  { value: "pendente", label: "Pendente", icon: "⏳", color: "text-yellow-600 bg-yellow-100", isActive: true },
  { value: "confirmado", label: "Confirmado", icon: "✓", color: "text-teal-600 bg-teal-100", isActive: true },
];

// Helper para obter info do status de participante
const getStatusInfo = (status) => {
  return PARTICIPANT_STATUS.find(s => s.value === status) || PARTICIPANT_STATUS[0];
};

// Helper para obter info do status do projeto
const getProjectStatusInfo = (status) => {
  return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
};

export default function SocialPage() {
  const { user, token } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Tab state
  const [activeTab, setActiveTab] = useState("projetos");
  
  // Blocos state
  const [blocos, setBlocos] = useState([]);
  const [blocoDialogOpen, setBlocoDialogOpen] = useState(false);
  const [editingBloco, setEditingBloco] = useState(null);
  const [blocoForm, setBlocoForm] = useState({
    nome: "",
    descricao: "",
    icone: "clipboard",
    cor: "bg-blue-500",
    exibir_no_news: false,
    formato_news: "cards",
    ordem: 0,
  });
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [addParticipantDialog, setAddParticipantDialog] = useState(false);
  const [participantSearch, setParticipantSearch] = useState("");
  const [leaderSearchResults, setLeaderSearchResults] = useState([]);
  const [responsavelSelecionado, setResponsavelSelecionado] = useState(null);
  const [criancasForm, setCriancasForm] = useState([{ nome: "", idade: "" }]);
  const [projectAppointments, setProjectAppointments] = useState([]);
  const [leaderSearch, setLeaderSearch] = useState("");
  const [trackingDialog, setTrackingDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDetailsDialog, setParticipantDetailsDialog] = useState(false);
  const [selectedParticipantDetails, setSelectedParticipantDetails] = useState(null);
  
  // Project modules states
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsProject, setSettingsProject] = useState(null);
  const [studentAreaModalOpen, setStudentAreaModalOpen] = useState(false);
  const [studentAreaProject, setStudentAreaProject] = useState(null);
  
  // Filtro para mostrar todos os status ou apenas ativos
  const [showAllParticipantStatus, setShowAllParticipantStatus] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    nome: "",
    tipo: "",
    icone: "default",
    lider_id: "",  // Deprecated - kept for backwards compatibility
    lider_nome: "",  // Deprecated - kept for backwards compatibility
    lideres: [],  // New: array of {id, nome}
    objetivo: "",
    data_inicio: "",
    status: "ativo",
    disponivel_inscricao: false,
    dias_horarios: "",
    local: "",
    faixa_etaria: "",
    tipo_doacao: "",
    publico_atendido: "",
    local_coleta: "",
    tipo_terapia: "",
    profissional_responsavel: "",
    area_curso: "",
    carga_horaria: "",
    modalidade: "",
    // Campos específicos de oração - configuração
    areas_oracao: [], // áreas disponíveis para seleção na inscrição
    // Campos de evento
    is_evento: false,
    data_evento: "",
    horario_evento: "",
    local_evento: "",
    gratuito: true,
    valor_inscricao: "",
    pix_chave: "",
    pix_titular: "",
    // WhatsApp
    enviar_confirmacao_whatsapp: false,
    // Disponibilizar para Escala ao Aprovar (novo campo simplificado)
    disponibilizar_escala_ao_aprovar: false,
    // Visível no filtro de Pessoas para líderes
    visivel_filtro_pessoas: false,
    // Bloco de inscrição personalizado
    bloco_inscricao: "",
    bloco_ordem: 0,
    // Configuração de inscrição externa
    campos_inscricao_externa: [],
  });
  
  const [ministries, setMinistries] = useState([]);
  
  const [trackingForm, setTrackingForm] = useState({
    data: new Date().toISOString().split("T")[0],
    descricao: "",
  });

  const canEdit = user?.nivel_acesso === "admin" || user?.permissoes?.social?.editar;
  const canCreate = user?.nivel_acesso === "admin" || user?.permissoes?.social?.criar;
  const canDelete = user?.nivel_acesso === "admin" || user?.permissoes?.social?.excluir;

  useEffect(() => {
    loadData();
  }, []);

  // Abrir projeto automaticamente se vier do Dashboard com openProjectId
  useEffect(() => {
    if (location.state?.openProjectId && projects.length > 0) {
      const projectToOpen = projects.find(p => p.id === location.state.openProjectId);
      if (projectToOpen) {
        setSelectedProject(projectToOpen);
        setDetailsDialog(true);
        // Limpar o state para não reabrir ao navegar
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, projects]);

  const loadData = async () => {
    try {
      // Use Promise.allSettled to handle 403 errors gracefully (members don't have access to /people)
      const results = await Promise.allSettled([
        axios.get(`${API}/projects`),
        axios.get(`${API}/people`),
        axios.get(`${API}/projects/stats`),
        axios.get(`${API}/ministries`),
        axios.get(`${API}/blocos-inscricao`),
      ]);
      
      // Projects - required
      if (results[0].status === "fulfilled") {
        setProjects(results[0].value.data);
      }
      
      // People - optional (members may not have access)
      if (results[1].status === "fulfilled") {
        setPeople(results[1].value.data);
      }
      
      // Stats - optional
      if (results[2].status === "fulfilled") {
        setStats(results[2].value.data);
      }
      
      // Ministries - optional
      if (results[3].status === "fulfilled") {
        setMinistries(results[3].value.data || []);
      }
      
      // Blocos - optional
      if (results[4].status === "fulfilled") {
        setBlocos(results[4].value.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeInfo = (tipo) => {
    return PROJECT_TYPES.find(t => t.value === tipo) || PROJECT_TYPES[0];
  };

  const filteredProjects = projects.filter(p => {
    if (searchTerm && !p.nome.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterType !== "all" && p.tipo !== filterType) {
      return false;
    }
    if (filterStatus !== "all" && p.status !== filterStatus) {
      return false;
    }
    return true;
  });

  const handleCreate = () => {
    setEditingProject(null);
    setForm({
      nome: "",
      tipo: "",
      icone: "default",
      lider_id: "",
      lider_nome: "",
      lideres: [],
      objetivo: "",
      data_inicio: "",
      status: "ativo",
      disponivel_inscricao: false,
      dias_horarios: "",
      local: "",
      faixa_etaria: "",
      tipo_doacao: "",
      publico_atendido: "",
      local_coleta: "",
      tipo_terapia: "",
      profissional_responsavel: "",
      area_curso: "",
      carga_horaria: "",
      modalidade: "",
      areas_oracao: [],
      // Evento
      is_evento: false,
      data_evento: "",
      horario_evento: "",
      local_evento: "",
      gratuito: true,
      valor_inscricao: "",
      // Outros
      enviar_confirmacao_whatsapp: false,
      disponibilizar_escala_ao_aprovar: false,
      visivel_filtro_pessoas: false,
      // Bloco
      bloco_inscricao: "",
      bloco_ordem: 0,
    });
    setDialogOpen(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setForm({
      nome: project.nome || "",
      tipo: project.tipo || "",
      icone: project.icone || "default",
      lider_id: project.lider_id || "",
      lider_nome: project.lider_nome || "",
      lideres: project.lideres || [],
      objetivo: project.objetivo || "",
      data_inicio: project.data_inicio || "",
      status: project.status || "ativo",
      disponivel_inscricao: project.disponivel_inscricao || false,
      dias_horarios: project.dias_horarios || "",
      local: project.local || "",
      faixa_etaria: project.faixa_etaria || "",
      tipo_doacao: project.tipo_doacao || "",
      publico_atendido: project.publico_atendido || "",
      local_coleta: project.local_coleta || "",
      tipo_terapia: project.tipo_terapia || "",
      profissional_responsavel: project.profissional_responsavel || "",
      area_curso: project.area_curso || "",
      carga_horaria: project.carga_horaria || "",
      modalidade: project.modalidade || "",
      areas_oracao: project.areas_oracao || [],
      // Campos de evento
      is_evento: project.is_evento || false,
      data_evento: project.data_evento || "",
      horario_evento: project.horario_evento || "",
      local_evento: project.local_evento || "",
      gratuito: project.gratuito !== false,
      valor_inscricao: project.valor_inscricao || "",
      pix_chave: project.pix_chave || "",
      pix_titular: project.pix_titular || "",
      // WhatsApp
      enviar_confirmacao_whatsapp: project.enviar_confirmacao_whatsapp || false,
      // Disponibilizar para Escala
      disponibilizar_escala_ao_aprovar: project.disponibilizar_escala_ao_aprovar || false,
      // Visível no filtro de Pessoas
      visivel_filtro_pessoas: project.visivel_filtro_pessoas || false,
      // Bloco de inscrição
      bloco_inscricao: project.bloco_inscricao || "",
      bloco_ordem: project.bloco_ordem || 0,
      // Campos de inscrição externa
      campos_inscricao_externa: project.campos_inscricao_externa || [],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome || !form.tipo) {
      toast.error("Nome e tipo são obrigatórios");
      return;
    }

    try {
      // Prepare data - convert empty strings to null and number fields
      const data = {
        ...form,
        lider_id: form.lider_id || null,
        lideres: form.lideres || [],
        valor_inscricao: form.valor_inscricao ? parseFloat(form.valor_inscricao) : null,
        pix_chave: form.pix_chave || null,
        pix_titular: form.pix_titular || null,
        data_inicio: form.data_inicio || null,
        data_evento: form.data_evento || null,
        horario_evento: form.horario_evento || null,
        local_evento: form.local_evento || null,
      };

      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, data);
        toast.success("Projeto atualizado!");
      } else {
        await axios.post(`${API}/projects`, data);
        toast.success("Projeto criado!");
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar projeto");
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Deseja excluir este projeto?")) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success("Projeto removido!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir projeto");
    }
  };

  // Check if user is a leader of the project
  const isProjectLeader = (project) => {
    return user?.nivel_acesso === "admin" || 
           project.lider_id === user?.pessoa_id ||
           project.lideres?.some(l => l.id === user?.pessoa_id);
  };

  // Pode gerenciar a inscrição aberta: permissão social.editar OU líder da inscrição
  const canManageProject = !!selectedProject && (canEdit || isProjectLeader(selectedProject));

  // Check if user is enrolled in the project
  const isProjectParticipant = (project) => {
    return project.participantes?.some(p => 
      p.pessoa_id === user?.pessoa_id || p.id === user?.pessoa_id
    );
  };

  const openDetails = (project) => {
    // If user is enrolled but NOT a leader, go to student area
    if (isProjectParticipant(project) && !isProjectLeader(project)) {
      setStudentAreaProject(project);
      setStudentAreaModalOpen(true);
      return;
    }
    // Otherwise, show details dialog (for leaders and non-enrolled users)
    setSelectedProject(project);
    setDetailsDialog(true);
  };

  const handleAddParticipant = async (pessoaId, criancas = null) => {
    try {
      const body = { pessoa_id: pessoaId };
      if (criancas) body.criancas = criancas.filter(c => c.nome.trim());
      const r = await axios.post(`${API}/projects/${selectedProject.id}/participants`, body);
      toast.success(r.data?.message || "Participante adicionado!");
      setAddParticipantDialog(false);
      setResponsavelSelecionado(null);
      setCriancasForm([{ nome: "", idade: "" }]);
      // Reload project details
      const res = await axios.get(`${API}/projects/${selectedProject.id}`);
      setSelectedProject(res.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar participante");
    }
  };

  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalParticipant, setApprovalParticipant] = useState(null);
  const [approvalMinistryId, setApprovalMinistryId] = useState("");

  const handleUpdateParticipantStatus = async (pessoaId, newStatus, ministerioDestinoId = null) => {
    // If changing to "aprovado" and project is audicao type, show ministry selection dialog
    if (newStatus === "aprovado" && selectedProject?.tipo === "audicao") {
      setApprovalParticipant({ pessoaId, newStatus });
      // Pre-select ministry if project has one linked
      const linkedMinistries = await getProjectLinkedMinistries(selectedProject.id);
      if (linkedMinistries.length > 0) {
        setApprovalMinistryId(linkedMinistries[0].id);
      } else {
        setApprovalMinistryId("");
      }
      setApprovalDialogOpen(true);
      return;
    }

    try {
      const payload = { 
        status: newStatus,
        registrar_historico: true  // Registra histórico de mudança de status
      };
      if (ministerioDestinoId) {
        payload.ministerio_destino_id = ministerioDestinoId;
      }
      
      // Se status é "atendido", finaliza a participação e permite nova inscrição
      if (newStatus === "atendido") {
        payload.finalizar_participacao = true;
      }
      
      await axios.put(`${API}/projects/${selectedProject.id}/participants/${pessoaId}`, payload);
      
      // Mensagem diferenciada para status atendido
      if (newStatus === "atendido") {
        toast.success("Participação finalizada! O membro pode se inscrever novamente.");
      } else {
        toast.success("Status atualizado!");
      }
      
      const res = await axios.get(`${API}/projects/${selectedProject.id}`);
      setSelectedProject(res.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar status");
    }
  };

  const getProjectLinkedMinistries = async (projectId) => {
    try {
      // Get ministries that have this project as audition project
      const ministriesRes = await axios.get(`${API}/ministries`);
      return ministriesRes.data.filter(m => m.projeto_audicao_id === projectId);
    } catch (error) {
      console.error("Error getting linked ministries:", error);
      return [];
    }
  };

  const handleConfirmApproval = async () => {
    if (!approvalParticipant) return;
    
    try {
      const payload = { 
        status: approvalParticipant.newStatus,
        ministerio_destino_id: approvalMinistryId || null
      };
      await axios.put(`${API}/projects/${selectedProject.id}/participants/${approvalParticipant.pessoaId}`, payload);
      toast.success("Aprovação realizada! Pessoa movida para 'Aguardando Ativação' no ministério.");
      const res = await axios.get(`${API}/projects/${selectedProject.id}`);
      setSelectedProject(res.data);
      loadData();
      setApprovalDialogOpen(false);
      setApprovalParticipant(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao aprovar participante");
    }
  };

  const handleRemoveParticipant = async (pessoaId) => {
    if (!window.confirm("Remover participante do projeto?")) return;
    
    try {
      await axios.delete(`${API}/projects/${selectedProject.id}/participants/${pessoaId}`);
      toast.success("Participante removido!");
      const res = await axios.get(`${API}/projects/${selectedProject.id}`);
      setSelectedProject(res.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao remover participante");
    }
  };

  const handleMarkAttended = async (pessoaId, atendido) => {
    try {
      await axios.put(`${API}/projects/${selectedProject.id}/participants/${pessoaId}/atendido`, {
        atendido
      });
      toast.success(atendido ? "Participante marcado como atendido!" : "Marcação removida");
      const res = await axios.get(`${API}/projects/${selectedProject.id}`);
      setSelectedProject(res.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar participante");
    }
  };

  const [historyDialog, setHistoryDialog] = useState(false);
  const [participantHistory, setParticipantHistory] = useState(null);
  
  const openHistoryDialog = async (participant) => {
    try {
      const res = await axios.get(`${API}/projects/${selectedProject.id}/participant-history/${participant.pessoa_id}`);
      setParticipantHistory(res.data);
      setHistoryDialog(true);
    } catch (error) {
      toast.error("Erro ao carregar histórico");
    }
  };

  const openTrackingDialog = (participant) => {
    setSelectedParticipant(participant);
    setTrackingForm({
      data: new Date().toISOString().split("T")[0],
      descricao: "",
    });
    setTrackingDialog(true);
  };

  const handleAddTracking = async () => {
    if (!trackingForm.descricao) {
      toast.error("Descrição é obrigatória");
      return;
    }
    
    try {
      await axios.post(
        `${API}/projects/${selectedProject.id}/participants/${selectedParticipant.pessoa_id}/acompanhamento`,
        trackingForm
      );
      toast.success("Acompanhamento registrado!");
      setTrackingDialog(false);
      const res = await axios.get(`${API}/projects/${selectedProject.id}`);
      setSelectedProject(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao registrar acompanhamento");
    }
  };

  const formatDate = (dateStr) => {
    return formatDateBR(dateStr);
  };

  const getAvailablePeople = () => {
    if (!selectedProject) return [];
    const participantIds = selectedProject.participantes?.map(p => p.pessoa_id) || [];
    const source = people.length > 0 ? people : leaderSearchResults;
    return source.filter(p => !participantIds.includes(p.id));
  };

  // Busca server-side para líderes sem permissão de ver Pessoas
  useEffect(() => {
    if (!addParticipantDialog || people.length > 0 || !selectedProject || participantSearch.trim().length < 2) {
      if (participantSearch.trim().length < 2) setLeaderSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/projects/${selectedProject.id}/people-search`, { params: { q: participantSearch.trim() } });
        setLeaderSearchResults(res.data || []);
      } catch (e) {
        setLeaderSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [participantSearch, addParticipantDialog, people.length, selectedProject]);

  const getWhatsAppLink = (telefone, text) => {
    const digits = (telefone || "").replace(/\D/g, "");
    if (!digits) return null;
    return `https://wa.me/${digits.length <= 11 ? "55" + digits : digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
  };

  const isTerapiaProject = selectedProject?.tipo === "terapias";
  const isInfantilProject = selectedProject?.tipo === "evento_infantil";
  const nextApptByPessoa = isTerapiaProject ? nextAppointmentByPessoa(projectAppointments) : {};
  const participantWhatsAppText = (participant) => {
    const appt = nextApptByPessoa[participant.pessoa_id];
    return appt ? appointmentMessage(appt, participant.pessoa_nome || participant.nome) : undefined;
  };

  useEffect(() => {
    if (detailsDialog && selectedProject?.tipo === "terapias") {
      axios.get(`${API}/projects/${selectedProject.id}/appointments`).then(r => setProjectAppointments(r.data || [])).catch(() => setProjectAppointments([]));
    } else {
      setProjectAppointments([]);
    }
  }, [detailsDialog, selectedProject?.id, selectedProject?.tipo]);

  // ============== BLOCOS CRUD ==============
  const handleCreateBloco = () => {
    setEditingBloco(null);
    setBlocoForm({
      nome: "",
      descricao: "",
      icone: "clipboard",
      cor: "bg-blue-500",
      exibir_no_news: false,
      formato_news: "cards",
      ordem: 0,
    });
    setBlocoDialogOpen(true);
  };

  const handleEditBloco = (bloco) => {
    setEditingBloco(bloco);
    setBlocoForm({
      nome: bloco.nome || "",
      descricao: bloco.descricao || "",
      icone: bloco.icone || "clipboard",
      cor: bloco.cor || "bg-blue-500",
      exibir_no_news: bloco.exibir_no_news || false,
      formato_news: bloco.formato_news || "cards",
      ordem: bloco.ordem || 0,
    });
    setBlocoDialogOpen(true);
  };

  const handleSaveBloco = async () => {
    if (!blocoForm.nome.trim()) {
      toast.error("Nome do bloco é obrigatório");
      return;
    }

    try {
      if (editingBloco) {
        await axios.put(`${API}/blocos-inscricao/${editingBloco.id}`, blocoForm);
        toast.success("Bloco atualizado!");
      } else {
        await axios.post(`${API}/blocos-inscricao`, blocoForm);
        toast.success("Bloco criado!");
      }
      setBlocoDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar bloco");
    }
  };

  const handleDeleteBloco = async (blocoId) => {
    if (!window.confirm("Deseja realmente excluir este bloco? Os projetos vinculados perderão a referência.")) {
      return;
    }
    try {
      await axios.delete(`${API}/blocos-inscricao/${blocoId}`);
      toast.success("Bloco excluído!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir bloco");
    }
  };

  const BLOCO_ICONS = [
    { value: "clipboard", label: "Lista", icon: ClipboardList },
    { value: "calendar", label: "Evento", icon: Calendar },
    { value: "heart", label: "Doação", icon: Heart },
    { value: "hand-heart", label: "Oração", icon: HandHeart },
    { value: "music", label: "Música", icon: Music },
    { value: "brain", label: "Terapia", icon: Brain },
    { value: "graduation", label: "Curso", icon: GraduationCap },
    { value: "dumbbell", label: "Esporte", icon: Dumbbell },
  ];

  const BLOCO_COLORS = [
    { value: "bg-blue-500", label: "Azul" },
    { value: "bg-purple-500", label: "Roxo" },
    { value: "bg-green-500", label: "Verde" },
    { value: "bg-amber-500", label: "Âmbar" },
    { value: "bg-red-500", label: "Vermelho" },
    { value: "bg-teal-500", label: "Teal" },
    { value: "bg-orange-500", label: "Laranja" },
    { value: "bg-indigo-500", label: "Índigo" },
  ];

  const getBlocoIcon = (iconName) => {
    const icon = BLOCO_ICONS.find(i => i.value === iconName);
    return icon ? icon.icon : ClipboardList;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      {/* Project Settings Modal */}
      <ProjectSettingsModal
        open={settingsModalOpen}
        onOpenChange={setSettingsModalOpen}
        project={settingsProject}
        onSettingsUpdated={() => {
          // Reload projects if needed
        }}
      />
      
      {/* Student Area Modal - renomeado para Área do Membro */}
      <StudentAreaModal
        open={studentAreaModalOpen}
        onOpenChange={setStudentAreaModalOpen}
        projectId={studentAreaProject?.id}
        projectTitle={studentAreaProject?.nome}
        projectType={studentAreaProject?.tipo}
      />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Inscrições</h1>
            <p className="text-muted-foreground">Gerencie projetos e blocos de inscrição</p>
          </div>
          {canCreate && (
            <div className="flex gap-2">
              {activeTab === "blocos" ? (
                <Button onClick={handleCreateBloco} className="gap-2" data-testid="new-bloco-btn">
                  <Plus className="h-4 w-4" />
                  Novo Bloco
                </Button>
              ) : (
                <Button onClick={handleCreate} className="gap-2" data-testid="new-project-btn">
                  <Plus className="h-4 w-4" />
                  Novo Projeto
                </Button>
              )}
            </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="projetos" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            Projetos
          </TabsTrigger>
          <TabsTrigger value="blocos" className="gap-2">
            <Package className="h-4 w-4" />
            Blocos
          </TabsTrigger>
        </TabsList>

        {/* Projetos Tab */}
        <TabsContent value="projetos" className="space-y-6 mt-6">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary">{stats.total_projects}</p>
                    <p className="text-sm text-muted-foreground">Total de Projetos</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-500">{stats.active_projects}</p>
                    <p className="text-sm text-muted-foreground">Projetos Ativos</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-500">{stats.total_participants}</p>
                    <p className="text-sm text-muted-foreground">Participantes</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-500">{Object.keys(stats.by_type || {}).length}</p>
                    <p className="text-sm text-muted-foreground">Tipos Ativos</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar projetos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {PROJECT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Projects Grid */}
          {filteredProjects.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Nenhum projeto encontrado</p>
                <p className="text-muted-foreground">
                  {canCreate ? "Crie seu primeiro projeto social" : "Não há projetos disponíveis"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProjects.map((project) => {
                const typeInfo = getTypeInfo(project.tipo);
                const statusInfo = getProjectStatusInfo(project.status);
                const TypeIcon = typeInfo.icon;
                
                return (
                  <Card 
                    key={project.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => openDetails(project)}
                    data-testid={`project-card-${project.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${typeInfo.color} text-white`}>
                            <TypeIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{project.nome}</CardTitle>
                            <p className="text-sm text-muted-foreground">{typeInfo.label}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col items-end gap-1">
                            <Badge className={statusInfo.color}>
                              {statusInfo.label}
                            </Badge>
                            {project.disponivel_inscricao && (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                                <ClipboardList className="h-3 w-3 mr-1" />
                                Inscrições Abertas
                              </Badge>
                            )}
                            {project.inscricao_automatica_ministerio && (
                              <Badge variant="outline" className="text-xs text-purple-600 border-purple-600">
                                <Users className="h-3 w-3 mr-1" />
                                Auto Ministério
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {project.objetivo && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {project.objetivo}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {(project.lideres?.length > 0 || project.lider_nome) && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {project.lideres?.length > 0 
                              ? project.lideres.map(l => l.nome).join(", ")
                              : project.lider_nome
                            }
                          </div>
                        )}
                        {project.data_inicio && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(project.data_inicio)}
                          </div>
                        )}
                        {project.local && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {project.local}
                          </div>
                        )}
                      </div>
                      
                      <Separator />
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{project.participantes?.length || 0} participantes</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Blocos Tab */}
        <TabsContent value="blocos" className="space-y-6 mt-6">
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Blocos de Inscrição
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Crie blocos para agrupar projetos na tela inicial e no News. Blocos marcados para exibir no News aparecerão na página pública.
                </p>
              </CardHeader>
              <CardContent>
                {blocos.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">Nenhum bloco criado</p>
                    <p className="text-muted-foreground mb-4">Crie blocos para organizar suas inscrições</p>
                    {canCreate && (
                      <Button onClick={handleCreateBloco} className="gap-2">
                        <Plus className="h-4 w-4" />
                        Criar Primeiro Bloco
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {blocos.map((bloco) => {
                      const IconComponent = getBlocoIcon(bloco.icone);
                      return (
                        <div key={bloco.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className={`p-2.5 rounded-lg ${bloco.cor} text-white`}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{bloco.nome}</h3>
                                {bloco.exibir_no_news && (
                                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-600">
                                    Visível no News
                                  </Badge>
                                )}
                              </div>
                              {bloco.descricao && (
                                <p className="text-sm text-muted-foreground">{bloco.descricao}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {bloco.projetos_count || 0} projeto(s) vinculado(s) • Ordem: {bloco.ordem}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {canEdit && (
                              <Button size="sm" variant="ghost" onClick={() => handleEditBloco(bloco)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteBloco(bloco.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="font-medium text-blue-800 dark:text-blue-200">Como funcionam os blocos?</p>
                    <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                      <li>Projetos com o mesmo bloco são agrupados na tela inicial (Dashboard)</li>
                      <li>Marque "Exibir no News" para mostrar o bloco na página pública de notícias</li>
                      <li>No News, visitantes poderão se inscrever diretamente no formulário público</li>
                      <li>Usuários logados serão direcionados automaticamente para a inscrição interna</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject ? "Editar Projeto" : "Novo Projeto"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Common Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome do Projeto *</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex: Escola de Música"
                />
              </div>
              
              <div>
                <Label>Tipo *</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Local</Label>
                <Input 
                  value={form.local || ""} 
                  onChange={(e) => setForm({ ...form, local: e.target.value })} 
                  placeholder="Endereço ou sala onde acontece"
                  data-testid="project-form-local"
                />
              </div>
              
              <div>
                <Label>Ícone Personalizado</Label>
                <Select value={form.icone} onValueChange={(v) => setForm({ ...form, icone: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ícone padrão" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_ICONS.map(iconOpt => (
                      <SelectItem key={iconOpt.value} value={iconOpt.value}>
                        <div className="flex items-center gap-2">
                          {iconOpt.icon && <iconOpt.icon className="h-4 w-4" />}
                          {iconOpt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2">
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div className="space-y-0.5">
                    <Label className="text-base">Disponível para Inscrição</Label>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, aparecerá um card na página inicial para os usuários se inscreverem
                    </p>
                  </div>
                  <Switch
                    checked={form.disponivel_inscricao}
                    onCheckedChange={(checked) => setForm({ ...form, disponivel_inscricao: checked })}
                  />
                </div>

                {/* Bloco de Inscrição - só aparece quando inscrição está ativada */}
                {form.disponivel_inscricao && (
                  <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20 space-y-3">
                    <h4 className="font-medium text-blue-800 dark:text-blue-200 flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Agrupamento no Dashboard
                    </h4>
                    <div>
                      <Label>Bloco de Inscrição</Label>
                      <Select 
                        value={form.bloco_inscricao || "__none__"} 
                        onValueChange={(v) => setForm({ ...form, bloco_inscricao: v === "__none__" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um bloco" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            <span className="text-muted-foreground">Agrupar por tipo (padrão)</span>
                          </SelectItem>
                          {blocos.map(bloco => (
                            <SelectItem key={bloco.id} value={bloco.nome}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded ${bloco.cor}`} />
                                {bloco.nome}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Selecione um bloco para agrupar este projeto com outros. <button type="button" onClick={() => setActiveTab("blocos")} className="text-blue-600 hover:underline">Criar novo bloco</button>
                      </p>
                    </div>
                    <div>
                      <Label>Ordem no Bloco</Label>
                      <Input
                        type="number"
                        value={form.bloco_ordem || 0}
                        onChange={(e) => setForm({ ...form, bloco_ordem: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Ordem de exibição dentro do bloco (menor número aparece primeiro)
                      </p>
                    </div>
                  </div>
                )}

                {/* Tipo: Evento com Data/Hora/Local/Valor */}
                {(form.tipo === "evento" || form.is_evento) && (
                  <div className="p-4 border rounded-lg bg-amber-50 dark:bg-amber-950/20 space-y-4">
                    <h4 className="font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Informações do Evento
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Data do Evento</Label>
                        <Input
                          type="date"
                          value={form.data_evento}
                          onChange={(e) => setForm({ ...form, data_evento: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Horário</Label>
                        <Input
                          type="time"
                          value={form.horario_evento}
                          onChange={(e) => setForm({ ...form, horario_evento: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Local do Evento</Label>
                      <Input
                        value={form.local_evento}
                        onChange={(e) => setForm({ ...form, local_evento: e.target.value })}
                        placeholder="Ex: Auditório Principal"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div>
                        <Label>Evento Gratuito</Label>
                        <p className="text-xs text-muted-foreground">Desative para informar valor</p>
                      </div>
                      <Switch
                        checked={form.gratuito}
                        onCheckedChange={(checked) => setForm({ ...form, gratuito: checked })}
                      />
                    </div>
                    {!form.gratuito && (
                      <div>
                        <Label>Valor da Inscrição (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.valor_inscricao}
                          onChange={(e) => setForm({ ...form, valor_inscricao: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                    )}
                    {!form.gratuito && (
                      <div className="p-3 border rounded-lg bg-green-50 dark:bg-green-950/20 space-y-3">
                        <h4 className="font-medium text-green-800 dark:text-green-200 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Dados do PIX para Pagamento
                        </h4>
                        <div>
                          <Label>Chave PIX</Label>
                          <Input
                            value={form.pix_chave || ""}
                            onChange={(e) => setForm({ ...form, pix_chave: e.target.value })}
                            placeholder="CPF, Email, Telefone ou Chave Aleatória"
                          />
                        </div>
                        <div>
                          <Label>Nome do Titular</Label>
                          <Input
                            value={form.pix_titular || ""}
                            onChange={(e) => setForm({ ...form, pix_titular: e.target.value })}
                            placeholder="Nome que aparece na conta"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Essas informações serão exibidas para os participantes no momento da inscrição.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Para tipos não-evento, opção de marcar como evento */}
                {form.tipo && form.tipo !== "evento" && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="text-sm">Também é um evento?</Label>
                      <p className="text-xs text-muted-foreground">Marque para adicionar data, horário e local</p>
                    </div>
                    <Switch
                      checked={form.is_evento}
                      onCheckedChange={(checked) => setForm({ ...form, is_evento: checked })}
                    />
                  </div>
                )}
              </div>
              
              <div className="col-span-2">
                <Label>Líderes</Label>
                
                {/* Líderes selecionados */}
                {form.lideres && form.lideres.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.lideres.map((lider) => (
                      <Badge key={lider.id} variant="secondary" className="flex items-center gap-1">
                        {lider.nome}
                        <button
                          type="button"
                          onClick={() => {
                            setForm({
                              ...form,
                              lideres: form.lideres.filter(l => l.id !== lider.id)
                            });
                          }}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Campo de busca */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar líder pelo nome..."
                    value={leaderSearch}
                    onChange={(e) => setLeaderSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Lista de resultados - só aparece durante busca */}
                {leaderSearch.length >= 2 && (
                  <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                    {people
                      .filter(p => 
                        p.nome_completo?.toLowerCase().includes(leaderSearch.toLowerCase()) &&
                        !form.lideres?.some(l => l.id === p.id)
                      )
                      .slice(0, 5)
                      .map(person => (
                      <div 
                        key={person.id} 
                        className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer"
                        onClick={() => {
                          setForm({
                            ...form,
                            lideres: [...(form.lideres || []), { id: person.id, nome: person.nome_completo }],
                            lider_id: form.lideres?.length === 0 ? person.id : form.lider_id,
                            lider_nome: form.lideres?.length === 0 ? person.nome_completo : form.lider_nome
                          });
                          setLeaderSearch("");
                        }}
                      >
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{person.nome_completo}</span>
                      </div>
                    ))}
                    {people.filter(p => 
                      p.nome_completo?.toLowerCase().includes(leaderSearch.toLowerCase()) &&
                      !form.lideres?.some(l => l.id === p.id)
                    ).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        Nenhuma pessoa encontrada
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Data de Início - ocultar para tipo evento */}
              {form.tipo !== "evento" && !form.is_evento && (
                <div>
                  <Label>Data de Início</Label>
                  <Input
                    type="date"
                    value={form.data_inicio}
                    onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                  />
                </div>
              )}
              
              <div className="col-span-2">
                <Label>Observações</Label>
                <Textarea
                  value={form.objetivo}
                  onChange={(e) => setForm({ ...form, objetivo: e.target.value })}
                  placeholder="Observações sobre o projeto"
                  rows={3}
                />
              </div>
            </div>
            
            <Separator />
            
            {/* Type-specific Fields */}
            {form.tipo && (
              <div className="space-y-4">
                <h4 className="font-medium">Campos específicos de {getTypeInfo(form.tipo).label}</h4>
                
                {/* Music */}
                {form.tipo === "musica" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Faixa Etária</Label>
                      <Select value={form.faixa_etaria} onValueChange={(v) => setForm({ ...form, faixa_etaria: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="criancas">Crianças</SelectItem>
                          <SelectItem value="jovens">Jovens</SelectItem>
                          <SelectItem value="adultos">Adultos</SelectItem>
                          <SelectItem value="geral">Geral</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Dias e Horários</Label>
                      <Input 
                        value={form.dias_horarios} 
                        onChange={(e) => setForm({ ...form, dias_horarios: e.target.value })}
                        placeholder="Ex: Segundas e Quartas, 19h às 21h"
                      />
                    </div>
                  </div>
                )}
                
                {/* Donations */}
                {form.tipo === "doacoes" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tipo de Doação</Label>
                      <Select value={form.tipo_doacao} onValueChange={(v) => setForm({ ...form, tipo_doacao: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alimentos">Alimentos</SelectItem>
                          <SelectItem value="roupas">Roupas</SelectItem>
                          <SelectItem value="brinquedos">Brinquedos</SelectItem>
                          <SelectItem value="dinheiro">Dinheiro</SelectItem>
                          <SelectItem value="outros">Outros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Público Atendido</Label>
                      <Input 
                        value={form.publico_atendido} 
                        onChange={(e) => setForm({ ...form, publico_atendido: e.target.value })}
                        placeholder="Ex: Famílias carentes"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Local de Coleta/Entrega</Label>
                      <Input 
                        value={form.local_coleta} 
                        onChange={(e) => setForm({ ...form, local_coleta: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                
                {/* Therapies */}
                {form.tipo === "terapias" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tipo de Terapia</Label>
                      <Select value={form.tipo_terapia} onValueChange={(v) => setForm({ ...form, tipo_terapia: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual">Individual</SelectItem>
                          <SelectItem value="grupo">Em Grupo</SelectItem>
                          <SelectItem value="psicologica">Psicológica</SelectItem>
                          <SelectItem value="ocupacional">Ocupacional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Profissional Responsável</Label>
                      <Input 
                        value={form.profissional_responsavel} 
                        onChange={(e) => setForm({ ...form, profissional_responsavel: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Dias e Horários</Label>
                      <Input 
                        value={form.dias_horarios} 
                        onChange={(e) => setForm({ ...form, dias_horarios: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                
                {/* Courses */}
                {form.tipo === "cursos" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Área/Nome do Curso</Label>
                      <Input 
                        value={form.area_curso} 
                        onChange={(e) => setForm({ ...form, area_curso: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Carga Horária</Label>
                      <Input 
                        value={form.carga_horaria} 
                        onChange={(e) => setForm({ ...form, carga_horaria: e.target.value })}
                        placeholder="Ex: 40h"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Dias e Horários</Label>
                      <Input 
                        value={form.dias_horarios} 
                        onChange={(e) => setForm({ ...form, dias_horarios: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                
                {/* Sports */}
                {form.tipo === "esporte" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Modalidade</Label>
                      <Input 
                        value={form.modalidade} 
                        onChange={(e) => setForm({ ...form, modalidade: e.target.value })}
                        placeholder="Ex: Futebol, Vôlei"
                      />
                    </div>
                    <div>
                      <Label>Faixa Etária</Label>
                      <Select value={form.faixa_etaria} onValueChange={(v) => setForm({ ...form, faixa_etaria: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="criancas">Crianças</SelectItem>
                          <SelectItem value="jovens">Jovens</SelectItem>
                          <SelectItem value="adultos">Adultos</SelectItem>
                          <SelectItem value="geral">Geral</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Dias e Horários</Label>
                      <Input 
                        value={form.dias_horarios} 
                        onChange={(e) => setForm({ ...form, dias_horarios: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                
                {/* Prayer / Oração */}
                {form.tipo === "oracao" && (
                  <div className="space-y-4">
                    <div>
                      <Label>Áreas disponíveis para pedido de oração</Label>
                      <p className="text-sm text-muted-foreground mb-2">
                        Selecione as áreas que estarão disponíveis para as pessoas escolherem na hora da inscrição
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {ORACAO_AREAS.map(area => (
                          <div
                            key={area.value}
                            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                              form.areas_oracao?.includes(area.value) 
                                ? 'bg-primary/10 border-primary' 
                                : 'hover:bg-muted'
                            }`}
                            onClick={() => {
                              const currentAreas = form.areas_oracao || [];
                              if (currentAreas.includes(area.value)) {
                                setForm({ ...form, areas_oracao: currentAreas.filter(a => a !== area.value) });
                              } else {
                                setForm({ ...form, areas_oracao: [...currentAreas, area.value] });
                              }
                            }}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                              form.areas_oracao?.includes(area.value) ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}>
                              {form.areas_oracao?.includes(area.value) && (
                                <CheckCircle className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <span className="text-sm">{area.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Configuração de Inscrição Externa - Agora no Modal de Configurações (ícone ⚙️) */}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 grid-cols-1 [&>*]:min-w-0" data-testid="project-details-dialog">
          {selectedProject && (
            <div className="min-w-0 w-full max-w-full overflow-hidden">
              <DialogHeader className="pr-8">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getTypeInfo(selectedProject.tipo).color} text-white`}>
                    {(() => { const Icon = getTypeInfo(selectedProject.tipo).icon; return <Icon className="h-5 w-5" />; })()}
                  </div>
                  <div>
                    <DialogTitle>{selectedProject.nome}</DialogTitle>
                    <p className="text-sm text-muted-foreground">{getTypeInfo(selectedProject.tipo).label}</p>
                  </div>
                </div>
              </DialogHeader>
              
              {/* Botões de ação - responsivos com overflow scroll no mobile */}
              {canManageProject && (
                <div className="flex gap-2 -mt-2 mb-2 border-b pb-3 overflow-x-auto" data-testid="details-action-bar">
                  <Button 
                    variant="outline" 
                    onClick={() => { 
                      setSettingsProject(selectedProject);
                      setSettingsModalOpen(true);
                    }}
                    data-testid="details-settings-btn"
                    className="shrink-0 h-12 w-12 sm:h-9 sm:w-auto sm:px-3 rounded-xl"
                    title="Configurações"
                  >
                    <Settings className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Configurações</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => { 
                      setStudentAreaProject(selectedProject);
                      setStudentAreaModalOpen(true);
                    }}
                    data-testid="details-member-area-btn"
                    className="shrink-0 h-12 w-12 sm:h-9 sm:w-auto sm:px-3 rounded-xl"
                    title="Área do Membro"
                  >
                    <BookOpen className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Área do Membro</span>
                  </Button>
                  <Button variant="outline" onClick={() => { setDetailsDialog(false); handleEdit(selectedProject); }} className="shrink-0 h-12 w-12 sm:h-9 sm:w-auto sm:px-3 rounded-xl" title="Editar" data-testid="details-edit-btn">
                    <Edit className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Editar</span>
                  </Button>
                  {canDelete && (
                    <Button variant="destructive" onClick={() => { setDetailsDialog(false); handleDelete(selectedProject.id); }} className="shrink-0 h-12 w-12 sm:h-9 sm:w-auto sm:px-3 rounded-xl ml-auto" title="Excluir" data-testid="details-delete-btn">
                      <Trash2 className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Excluir</span>
                    </Button>
                  )}
                </div>
              )}
              
              <Tabs defaultValue="participants" className="mt-4">
                <TabsList className="flex gap-2 p-1 bg-muted/50 rounded-full overflow-x-auto w-fit max-w-full">
                  <TabsTrigger 
                    value="participants" 
                    className="rounded-full px-4 py-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap"
                  >
                    <Users className="h-3.5 w-3.5 mr-1.5" />
                    Membros ({selectedProject.participantes?.length || 0})
                  </TabsTrigger>
                  {/* Aba Agenda - terapias (hora marcada) */}
                  {canManageProject && isTerapiaProject && (
                    <TabsTrigger 
                      value="agenda" 
                      className="rounded-full px-4 py-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap"
                      data-testid="tab-agenda"
                    >
                      <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
                      Agenda
                    </TabsTrigger>
                  )}
                  {/* Aba Frequência - apenas para líderes, módulo ativo e não-terapia */}
                  {canManageProject && !isTerapiaProject && selectedProject?.module_settings?.presenca_habilitada && (
                    <TabsTrigger 
                      value="frequencia" 
                      className="rounded-full px-4 py-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap"
                    >
                      <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                      Frequência
                    </TabsTrigger>
                  )}
                  {(selectedProject?.gratuito === false || selectedProject?.valor_inscricao > 0) && (
                    <TabsTrigger 
                      value="pagamentos" 
                      className="rounded-full px-4 py-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap"
                    >
                      <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                      Pagamentos
                    </TabsTrigger>
                  )}
                </TabsList>
                
                <TabsContent value="participants" className="space-y-4">
                  {/* Header com botão de adicionar e filtro de status */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                    {canManageProject && (
                      <Button onClick={() => setAddParticipantDialog(true)} className="gap-2 w-full sm:w-auto" data-testid="add-participant-btn">
                        <UserPlus className="h-4 w-4" />
                        Adicionar Participante
                      </Button>
                    )}
                    
                    {/* Checkbox para mostrar todos os status */}
                    <div className="flex items-center gap-2 sm:ml-auto min-w-0">
                      <Checkbox
                        id="show-all-status"
                        checked={showAllParticipantStatus}
                        onCheckedChange={setShowAllParticipantStatus}
                      />
                      <Label 
                        htmlFor="show-all-status" 
                        className="text-xs sm:text-sm cursor-pointer text-muted-foreground truncate"
                      >
                        Mostrar inativos/atendidos
                      </Label>
                    </div>
                  </div>
                  
                  {(() => {
                    // Filtra participantes baseado no checkbox
                    const filteredParticipants = showAllParticipantStatus 
                      ? selectedProject.participantes 
                      : selectedProject.participantes?.filter(p => {
                          const statusInfo = getStatusInfo(p.status);
                          return statusInfo.isActive || !p.status; // Mostra ativos e sem status
                        });
                    
                    const hiddenCount = (selectedProject.participantes?.length || 0) - (filteredParticipants?.length || 0);
                    
                    return (
                      <>
                        {/* Contador de ocultos */}
                        {hiddenCount > 0 && !showAllParticipantStatus && (
                          <p className="text-xs text-muted-foreground text-center">
                            {hiddenCount} membro{hiddenCount > 1 ? 's' : ''} oculto{hiddenCount > 1 ? 's' : ''} (atendidos/inativos)
                          </p>
                        )}
                        
                        {filteredParticipants?.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Nenhum participante {showAllParticipantStatus ? 'cadastrado' : 'ativo'}</p>
                          </div>
                        ) : (
                          <div className="max-h-[55vh] sm:max-h-[400px] overflow-y-auto pr-1">
                            <div className="space-y-2">
                              {filteredParticipants?.map((participant, idx) => {
                                const statusInfo = getStatusInfo(participant.status);
                                
                                return (
                                  <div 
                                    key={idx} 
                                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                      participant.status === "convidado" ? "border-pink-200 bg-pink-50/50" : 
                                      participant.status === "atendido" ? "border-purple-200 bg-purple-50/30" :
                                      participant.status === "inativo" ? "border-gray-200 bg-gray-50/30" :
                                      "bg-card hover:bg-muted/30"
                                    }`}
                                  >
                                    {/* Avatar/Ícone */}
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                      participant.status === "convidado" ? "bg-pink-100" : "bg-primary/10"
                                    }`}>
                                      <User className={`h-5 w-5 ${
                                        participant.status === "convidado" ? "text-pink-600" : "text-primary"
                                      }`} />
                                    </div>
                                    
                                    {/* Nome e info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-medium text-sm truncate">
                                          {participant.crianca && <Baby className="inline h-3.5 w-3.5 mr-1 text-yellow-600" />}
                                          {participant.pessoa_nome || participant.nome || `Participante ${participant.telefone || ''}`}
                                        </p>
                                        {participant.crianca && participant.responsavel_nome && (
                                          <Badge variant="outline" className="text-[10px] shrink-0 bg-yellow-50 border-yellow-200 text-yellow-800" data-testid={`child-responsible-${participant.pessoa_id}`}>
                                            Resp.: {participant.responsavel_nome}{participant.idade ? ` · ${participant.idade} anos` : ""}
                                          </Badge>
                                        )}
                                        {participant.numero_inscricao && (
                                          <Badge variant="outline" className="text-[10px] shrink-0">
                                            {participant.numero_inscricao}
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        Inscrito em {formatDate(participant.data_inscricao)}
                                      </p>
                                      {/* Convidado info */}
                                      {participant.convidado_nome && (
                                        <p className="text-xs text-pink-600 mt-0.5">
                                          👥 {participant.convidado_nome}
                                        </p>
                                      )}
                                      {/* Indicador de detalhes */}
                                      {(participant.observacoes || (participant.campos_personalizados && Object.keys(participant.campos_personalizados).length > 0)) && (
                                        <Badge variant="outline" className="mt-1 text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                                          <FileText className="h-2.5 w-2.5 mr-0.5" />
                                          Detalhes
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    {/* Ações */}
                                    <div className="flex items-center gap-1 shrink-0">
                                      {/* Atalho WhatsApp */}
                                      {canManageProject && getWhatsAppLink(participant.telefone) && (
                                        <Button
                                          asChild
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                          title={nextApptByPessoa[participant.pessoa_id] ? `Lembrar sessão de ${nextApptByPessoa[participant.pessoa_id].data.split("-").reverse().join("/")} às ${nextApptByPessoa[participant.pessoa_id].hora}` : participant.crianca ? `WhatsApp do responsável (${participant.responsavel_nome}): ${participant.telefone}` : `WhatsApp: ${participant.telefone}`}
                                          data-testid={`participant-whatsapp-btn-${participant.pessoa_id}`}
                                        >
                                          <a href={getWhatsAppLink(participant.telefone, participantWhatsAppText(participant))} target="_blank" rel="noopener noreferrer">
                                            <MessageCircle className="h-4 w-4" />
                                          </a>
                                        </Button>
                                      )}
                                      
                                      {/* Select de Status com ícone */}
                                      {canManageProject && (
                                        <Select 
                                          value={participant.status || "ativo"} 
                                          onValueChange={(v) => handleUpdateParticipantStatus(participant.pessoa_id, v)}
                                        >
                                          <SelectTrigger className="w-[60px] sm:w-[110px] h-8 text-xs px-2">
                                            <span className="flex items-center gap-1.5">
                                              <span>{statusInfo.icon}</span>
                                              <span className="hidden sm:inline">{statusInfo.label}</span>
                                            </span>
                                          </SelectTrigger>
                                          <SelectContent>
                                            {PARTICIPANT_STATUS.map(s => (
                                              <SelectItem key={s.value} value={s.value}>
                                                <span className="flex items-center gap-2">
                                                  <span>{s.icon}</span>
                                                  <span>{s.label}</span>
                                                </span>
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      )}
                                      
                                      {/* Botão Ver Detalhes */}
                                      {canManageProject && (participant.observacoes || (participant.campos_personalizados && Object.keys(participant.campos_personalizados).length > 0)) && (
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => {
                                            setSelectedParticipantDetails(participant);
                                            setParticipantDetailsDialog(true);
                                          }}
                                          title="Ver detalhes"
                                        >
                                          <FileText className="h-4 w-4 text-indigo-600" />
                                        </Button>
                                      )}
                                      
                                      {/* Botão Excluir */}
                                      {canManageProject && (
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          className="h-8 w-8 text-destructive hover:text-destructive"
                                          onClick={() => handleRemoveParticipant(participant.pessoa_id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </TabsContent>
                
                {/* Tab Agenda - terapias */}
                {canManageProject && isTerapiaProject && (
                  <TabsContent value="agenda" className="space-y-4">
                    <ProjectAgendaTab
                      project={selectedProject}
                      participants={selectedProject.participantes || []}
                      canManage={canManageProject}
                      onChanged={setProjectAppointments}
                    />
                  </TabsContent>
                )}

                {/* Tab de Frequência - só para líderes, módulo ativo e não-terapia */}
                {canManageProject && !isTerapiaProject && selectedProject?.module_settings?.presenca_habilitada && (
                  <TabsContent value="frequencia" className="space-y-4">
                    <FrequenciaTab 
                      project={selectedProject}
                      token={token}
                      isTerapia={selectedProject.tipo === "terapias"}
                      onRefresh={async () => {
                        const res = await axios.get(`${API}/projects/${selectedProject.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setSelectedProject(res.data);
                      }}
                    />
                  </TabsContent>
                )}
                
                {/* Tab de Pagamentos - só aparece para projetos pagos */}
                {(selectedProject?.gratuito === false || selectedProject?.valor_inscricao > 0) && (
                  <TabsContent value="pagamentos" className="space-y-4">
                    <PagamentosTab 
                      project={selectedProject} 
                      token={token}
                      canEdit={canManageProject}
                      onRefresh={async () => {
                        const res = await axios.get(`${API}/projects/${selectedProject.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setSelectedProject(res.data);
                      }}
                    />
                  </TabsContent>
                )}
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Participant Dialog - Com busca */}
      <Dialog open={addParticipantDialog} onOpenChange={(open) => {
        setAddParticipantDialog(open);
        if (!open) setParticipantSearch("");
      }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{isInfantilProject ? (responsavelSelecionado ? "Crianças do responsável" : "Selecione o responsável") : "Adicionar Participante"}</DialogTitle>
          </DialogHeader>

          {isInfantilProject && responsavelSelecionado && (
            <div className="space-y-3" data-testid="children-form">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm">
                <User className="h-4 w-4 text-primary" />
                <span className="font-medium truncate">{responsavelSelecionado.nome_completo}</span>
                <span className="text-xs text-muted-foreground truncate">{responsavelSelecionado.telefone}</span>
                <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => setResponsavelSelecionado(null)}>Trocar</Button>
              </div>
              {criancasForm.map((c, i) => (
                <div key={i} className="flex gap-2 items-end">
                  <div className="flex-1 min-w-0">
                    <Label className="text-xs">Nome da criança *</Label>
                    <Input value={c.nome} onChange={(e) => setCriancasForm(prev => prev.map((x, j) => j === i ? { ...x, nome: e.target.value } : x))} placeholder="Nome completo" data-testid={`child-name-${i}`} />
                  </div>
                  <div className="w-[84px] shrink-0">
                <Label className="text-xs">Idade</Label>
                <Input type="number" inputMode="numeric" min={0} max={17} placeholder="anos" value={c.idade} onChange={(e) => setCriancasForm(prev => prev.map((x, j) => j === i ? { ...x, idade: e.target.value } : x))} className="min-w-0" data-testid={`child-age-${i}`} />
              </div>
                  {criancasForm.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 shrink-0" onClick={() => setCriancasForm(prev => prev.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" className="w-full" onClick={() => setCriancasForm(prev => [...prev, { nome: "", idade: "" }])} data-testid="child-add-row">
                <Plus className="h-4 w-4 mr-1" /> Outra criança
              </Button>
              <Button className="w-full" disabled={!criancasForm.some(c => c.nome.trim())} onClick={() => handleAddParticipant(responsavelSelecionado.id, criancasForm)} data-testid="children-save-btn">
                <Baby className="h-4 w-4 mr-2" /> Inscrever {criancasForm.filter(c => c.nome.trim()).length || ""} criança(s)
              </Button>
            </div>
          )}
          
          {/* Campo de busca */}
          {!(isInfantilProject && responsavelSelecionado) && (<>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar pessoa pelo nome..."
              value={participantSearch}
              onChange={(e) => setParticipantSearch(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>
          
          {/* Lista de resultados - só aparece se houver busca */}
          {participantSearch.length >= 2 && (
            <ScrollArea className="h-[250px]">
              <div className="space-y-1">
                {getAvailablePeople()
                  .filter(p => p.nome_completo?.toLowerCase().includes(participantSearch.toLowerCase()))
                  .slice(0, 10)
                  .map(person => (
                  <div 
                    key={person.id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted cursor-pointer"
                    onClick={() => {
                      if (isInfantilProject) {
                        setResponsavelSelecionado(person);
                      } else {
                        handleAddParticipant(person.id);
                      }
                      setParticipantSearch("");
                    }}
                    data-testid={`add-participant-option-${person.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{person.nome_completo}</p>
                        <p className="text-xs text-muted-foreground">{person.email || person.telefone}</p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
                
                {getAvailablePeople().filter(p => p.nome_completo?.toLowerCase().includes(participantSearch.toLowerCase())).length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma pessoa encontrada
                  </p>
                )}
              </div>
            </ScrollArea>
          )}
          
          {participantSearch.length < 2 && (
            <p className="text-center text-muted-foreground py-6 text-sm">
              {isInfantilProject ? "Busque o responsável pelo nome (as crianças serão adicionadas em seguida)" : "Digite pelo menos 2 caracteres para buscar"}
            </p>
          )}
          </>)}
        </DialogContent>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog open={trackingDialog} onOpenChange={setTrackingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Acompanhamento</DialogTitle>
          </DialogHeader>
          
          {selectedParticipant && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Participante: <strong>{selectedParticipant.pessoa_nome}</strong>
              </p>
              
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={trackingForm.data}
                  onChange={(e) => setTrackingForm({ ...trackingForm, data: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Descrição *</Label>
                <Textarea
                  value={trackingForm.descricao}
                  onChange={(e) => setTrackingForm({ ...trackingForm, descricao: e.target.value })}
                  placeholder="Descreva a aula, sessão ou atividade realizada"
                  rows={4}
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setTrackingDialog(false)}>Cancelar</Button>
                <Button onClick={handleAddTracking}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Registrar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Histórico de Inscrições
            </DialogTitle>
          </DialogHeader>
          
          {participantHistory && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Participante: <strong>{participantHistory.pessoa?.nome}</strong>
              </p>
              
              {participantHistory.historico?.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  Nenhum histórico encontrado
                </p>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {participantHistory.historico?.map((item, idx) => (
                      <div key={idx} className="p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">{item.projeto_nome}</p>
                          <Badge variant={item.status === "atendido" ? "default" : "secondary"} className="text-xs">
                            {item.status === "atendido" ? (
                              <>
                                <CheckCheck className="h-3 w-3 mr-1" />
                                Atendido
                              </>
                            ) : item.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Inscrito em {formatDate(item.data_inscricao)}
                        </p>
                        {item.data_atendimento && (
                          <p className="text-xs text-green-600 mt-1">
                            Atendido em {formatDate(item.data_atendimento)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bloco Create/Edit Dialog */}
      <Dialog open={blocoDialogOpen} onOpenChange={setBlocoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingBloco ? "Editar Bloco" : "Novo Bloco de Inscrição"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome do Bloco *</Label>
              <Input
                value={blocoForm.nome}
                onChange={(e) => setBlocoForm({ ...blocoForm, nome: e.target.value })}
                placeholder="Ex: Instituto Life, Eventos Especiais"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={blocoForm.descricao}
                onChange={(e) => setBlocoForm({ ...blocoForm, descricao: e.target.value })}
                placeholder="Descrição opcional do bloco"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ícone</Label>
                <Select value={blocoForm.icone} onValueChange={(v) => setBlocoForm({ ...blocoForm, icone: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOCO_ICONS.map(icon => (
                      <SelectItem key={icon.value} value={icon.value}>
                        <div className="flex items-center gap-2">
                          <icon.icon className="h-4 w-4" />
                          {icon.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Cor</Label>
                <Select value={blocoForm.cor} onValueChange={(v) => setBlocoForm({ ...blocoForm, cor: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOCO_COLORS.map(cor => (
                      <SelectItem key={cor.value} value={cor.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${cor.value}`} />
                          {cor.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Ordem de Exibição</Label>
              <Input
                type="number"
                value={blocoForm.ordem}
                onChange={(e) => setBlocoForm({ ...blocoForm, ordem: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground mt-1">Menor número aparece primeiro</p>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Exibir no News (Página Pública)</Label>
                  <p className="text-xs text-muted-foreground">
                    Visitantes poderão ver e se inscrever nos projetos deste bloco
                  </p>
                </div>
                <Switch
                  checked={blocoForm.exibir_no_news}
                  onCheckedChange={(v) => setBlocoForm({ ...blocoForm, exibir_no_news: v })}
                />
              </div>

              {blocoForm.exibir_no_news && (
                <div>
                  <Label>Formato de Exibição no News</Label>
                  <Select value={blocoForm.formato_news} onValueChange={(v) => setBlocoForm({ ...blocoForm, formato_news: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cards">Cards (Grade)</SelectItem>
                      <SelectItem value="lista">Lista Compacta</SelectItem>
                      <SelectItem value="destaque">Destaque (Banner)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBlocoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveBloco}>
              {editingBloco ? "Salvar" : "Criar Bloco"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog - Select Ministry when approving audition */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aprovar na Audição</DialogTitle>
            <DialogDescription>
              Selecione para qual ministério/equipe esta pessoa será encaminhada após a aprovação.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Ministério de Destino</Label>
              <Select value={approvalMinistryId} onValueChange={setApprovalMinistryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o ministério" />
                </SelectTrigger>
                <SelectContent>
                  {ministries.filter(m => m.precisa_audicao).map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                  ))}
                  <SelectItem value="">Nenhum (manter padrão)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                A pessoa será movida para "Aguardando Ativação" no ministério selecionado.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmApproval}>
              Confirmar Aprovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Participant Details Dialog - Para Pedido de Oração e outros campos personalizados */}
      <Dialog open={participantDetailsDialog} onOpenChange={setParticipantDetailsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Detalhes da Inscrição
            </DialogTitle>
            <DialogDescription>
              Informações preenchidas pelo participante
            </DialogDescription>
          </DialogHeader>
          
          {selectedParticipantDetails && (
            <div className="space-y-4 py-4">
              {/* Nome do participante */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{selectedParticipantDetails.pessoa_nome}</p>
                  <p className="text-xs text-muted-foreground">
                    Inscrito em {formatDate(selectedParticipantDetails.data_inscricao)}
                  </p>
                </div>
              </div>
              
              {/* Observações */}
              {selectedParticipantDetails.observacoes && (
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                  <Label className="text-yellow-800 flex items-center gap-1 mb-2">
                    <FileText className="h-4 w-4" />
                    Observação
                  </Label>
                  <p className="text-sm text-yellow-900 whitespace-pre-wrap">
                    {selectedParticipantDetails.observacoes}
                  </p>
                </div>
              )}
              
              {/* Campos Personalizados (Motivo, Descrição, etc.) */}
              {selectedParticipantDetails.campos_personalizados && Object.keys(selectedParticipantDetails.campos_personalizados).length > 0 && (
                <div className="space-y-3">
                  <Label className="text-indigo-800 flex items-center gap-1">
                    <ClipboardList className="h-4 w-4" />
                    Informações do Pedido
                  </Label>
                  {Object.entries(selectedParticipantDetails.campos_personalizados).map(([key, value]) => (
                    value && (
                      <div key={key} className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                        <Label className="text-indigo-700 capitalize text-xs">
                          {key.replace(/_/g, ' ')}
                        </Label>
                        <p className="text-sm text-indigo-900 mt-1 whitespace-pre-wrap">
                          {value}
                        </p>
                      </div>
                    )
                  ))}
                </div>
              )}
              
              {/* Informações adicionais */}
              {selectedParticipantDetails.forma_pagamento && (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                  <Label className="text-green-800 flex items-center gap-1 mb-1">
                    <CreditCard className="h-4 w-4" />
                    Pagamento
                  </Label>
                  <p className="text-sm text-green-900">
                    {selectedParticipantDetails.forma_pagamento}
                    {selectedParticipantDetails.data_pagamento && ` em ${formatDate(selectedParticipantDetails.data_pagamento)}`}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setParticipantDetailsDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}

// Componente da aba de Frequência/Sessões - para líderes
function FrequenciaTab({ project, token, isTerapia = false, onRefresh }) {
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [presencas, setPresencas] = useState({});

  useEffect(() => {
    loadAttendanceData();
  }, [project?.id]);

  const loadAttendanceData = async () => {
    if (!project?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/projects/${project.id}/attendance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceData(res.data);
      
      // Set default selected date to today or closest
      const today = new Date().toISOString().split('T')[0];
      const dates = res.data?.encontros?.map(e => e.data) || [];
      const closestDate = dates.find(d => d >= today) || dates[dates.length - 1];
      if (closestDate) {
        setSelectedDate(closestDate);
        // Load presences for this date
        const encontro = res.data?.encontros?.find(e => e.data === closestDate);
        if (encontro?.presencas) {
          const presMap = {};
          encontro.presencas.forEach(p => {
            presMap[p.pessoa_id] = p.presente;
          });
          setPresencas(presMap);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar frequência:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // Load presences for this date
    const encontro = attendanceData?.encontros?.find(e => e.data === date);
    if (encontro?.presencas) {
      const presMap = {};
      encontro.presencas.forEach(p => {
        presMap[p.pessoa_id] = p.presente;
      });
      setPresencas(presMap);
    } else {
      setPresencas({});
    }
  };

  const togglePresence = (pessoaId) => {
    setPresencas(prev => ({
      ...prev,
      [pessoaId]: !prev[pessoaId]
    }));
  };

  const savePresences = async () => {
    if (!selectedDate) return;
    setSaving(true);
    try {
      const presencaList = Object.entries(presencas).map(([pessoa_id, presente]) => ({
        pessoa_id,
        presente
      }));
      
      await axios.post(`${API}/projects/${project.id}/attendance/record`, {
        data: selectedDate,
        presencas: presencaList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Frequência salva!");
      loadAttendanceData();
    } catch (error) {
      toast.error("Erro ao salvar frequência");
    } finally {
      setSaving(false);
    }
  };

  // Filter participants by search
  const filteredParticipants = project?.participantes?.filter(p => {
    const nome = p.pessoa_nome || p.nome || p.telefone || '';
    return !searchTerm || nome.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  // Format date for display
  const formatDateShort = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });
  };

  const isToday = (dateStr) => {
    return dateStr === new Date().toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!attendanceData?.encontros?.length) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>{isTerapia ? "Nenhuma sessão configurada" : "Nenhuma lista de presença configurada"}</p>
        <p className="text-sm mt-2">Configure {isTerapia ? "as sessões" : "a lista de presença"} nas Configurações</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date selector */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{isTerapia ? "Selecione a Sessão" : "Selecione a Data"}</Label>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {attendanceData.encontros.map((encontro) => (
            <Button
              key={encontro.data}
              variant={selectedDate === encontro.data ? "default" : "outline"}
              size="sm"
              className={`whitespace-nowrap ${isToday(encontro.data) ? "ring-2 ring-primary" : ""}`}
              onClick={() => handleDateChange(encontro.data)}
            >
              {formatDateShort(encontro.data)}
              {isToday(encontro.data) && <span className="ml-1 text-xs">(Hoje)</span>}
            </Button>
          ))}
        </div>
      </div>

      {/* Search field */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={isTerapia ? "Buscar paciente..." : "Buscar participante..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Participants list with checkboxes */}
      <ScrollArea className="h-[250px] border rounded-lg">
        <div className="p-2 space-y-1">
          {filteredParticipants.length === 0 ? (
            <p className="text-center text-muted-foreground p-4">
              {searchTerm 
                ? (isTerapia ? "Nenhum paciente encontrado" : "Nenhum participante encontrado")
                : (isTerapia ? "Nenhum paciente inscrito" : "Nenhum participante inscrito")
              }
            </p>
          ) : (
            filteredParticipants.map((participant) => (
              <div
                key={participant.id || participant.pessoa_id}
                className={`flex items-center justify-between p-2 sm:p-3 rounded-lg cursor-pointer transition-colors ${
                  presencas[participant.pessoa_id || participant.id] 
                    ? "bg-green-50 hover:bg-green-100 border border-green-200" 
                    : "bg-muted/30 hover:bg-muted/50 border border-transparent"
                }`}
                onClick={() => togglePresence(participant.pessoa_id || participant.id)}
              >
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                    presencas[participant.pessoa_id || participant.id] 
                      ? "bg-green-500 border-green-500" 
                      : "border-gray-300"
                  }`}>
                    {presencas[participant.pessoa_id || participant.id] && (
                      <CheckCircle className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <span className="font-medium text-sm truncate">{participant.pessoa_nome || participant.nome || `Participante ${participant.telefone || ''}`}</span>
                </div>
                <Badge variant={presencas[participant.pessoa_id || participant.id] ? "default" : "secondary"} className="flex-shrink-0 ml-2 text-xs">
                  {presencas[participant.pessoa_id || participant.id] ? "✓" : "—"}
                </Badge>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Save button */}
      <Button onClick={savePresences} disabled={saving || !selectedDate} className="w-full">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        {isTerapia ? "Salvar Sessão" : "Salvar Frequência"}
      </Button>
    </div>
  );
}

// Componente da aba de Pagamentos
function PagamentosTab({ project, token, canEdit, onRefresh }) {
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPagamentos();
  }, [project?.id]);

  const loadPagamentos = async () => {
    if (!project?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/projects/${project.id}/pagamentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPagamentos(res.data.pagamentos || []);
    } catch (error) {
      console.error("Erro ao carregar pagamentos:", error);
      toast.error("Erro ao carregar pagamentos");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarPagamento = async (pessoaId) => {
    try {
      await axios.put(
        `${API}/financeiro/confirmar-pagamento/${project.id}/${pessoaId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pagamento confirmado!");
      loadPagamentos();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao confirmar pagamento");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return formatDateBR(dateStr);
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return formatDateTimeBR(dateStr);
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pendente":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pendente</Badge>;
      case "confirmado":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Confirmado</Badge>;
      case "baixado":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Baixado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Estatísticas
  const totalParticipantes = pagamentos.length;
  const pendentes = pagamentos.filter(p => p.status_pagamento === "pendente" && !p.is_convidado).length;
  const confirmados = pagamentos.filter(p => p.status_pagamento === "confirmado" && !p.is_convidado).length;
  const baixados = pagamentos.filter(p => p.status_pagamento === "baixado" && !p.is_convidado).length;
  // Calcular valores usando o campo 'valor' que vem da API (0 para convidados)
  const valorTotal = pagamentos.reduce((acc, p) => acc + (p.valor || 0), 0);
  const valorRecebido = pagamentos
    .filter(p => p.status_pagamento === "confirmado" || p.status_pagamento === "baixado")
    .reduce((acc, p) => acc + (p.valor || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cards de resumo */}
      <div className="grid grid-cols-2 gap-2">
        <Card className="p-3">
          <div className="text-sm text-muted-foreground">Total Esperado</div>
          <div className="text-lg font-bold text-green-600">
            R$ {valorTotal.toFixed(2).replace(".", ",")}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-muted-foreground">Confirmado</div>
          <div className="text-lg font-bold text-blue-600">
            R$ {valorRecebido.toFixed(2).replace(".", ",")}
          </div>
        </Card>
      </div>
      
      {/* Status badges */}
      <div className="flex gap-2 flex-wrap">
        <Badge variant="outline">
          Total: {totalParticipantes}
        </Badge>
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
          Pendentes: {pendentes}
        </Badge>
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          Confirmados: {confirmados}
        </Badge>
        <Badge variant="outline" className="bg-green-50 text-green-700">
          Baixados: {baixados}
        </Badge>
      </div>
      
      {/* Lista de pagamentos */}
      {pagamentos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>Nenhuma inscrição ainda</p>
        </div>
      ) : (
        <ScrollArea className="h-[300px]">
          <div className="space-y-2">
            {pagamentos.map((pag) => (
              <Card key={pag.pessoa_id} className={pag.is_convidado ? "border-pink-200 bg-pink-50/50" : ""}>
                <div className="p-3 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <User className={`h-4 w-4 ${pag.is_convidado ? "text-pink-600" : "text-muted-foreground"}`} />
                      <span className="font-medium">{pag.pessoa_nome}</span>
                      {pag.is_convidado && (
                        <Badge variant="outline" className="bg-pink-100 text-pink-700 border-pink-300 text-xs">
                          Convidado
                        </Badge>
                      )}
                      {pag.numero_inscricao && (
                        <Badge variant="outline" className="text-xs">
                          {pag.numero_inscricao}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                      <div>Inscrito em: {formatDate(pag.data_inscricao)}</div>
                      {pag.forma_pagamento && !pag.is_convidado && (
                        <div className="flex items-center gap-1">
                          <CreditCard className="h-3 w-3" />
                          {pag.forma_pagamento}
                        </div>
                      )}
                      {pag.convidado_nome && (
                        <div className="text-pink-600 flex items-center gap-1">
                          <Users2 className="h-3 w-3" />
                          Convidado: {pag.convidado_nome}
                        </div>
                      )}
                      {pag.pagamento_confirmado_em && (
                        <div className="text-blue-600">
                          Confirmado por {pag.pagamento_confirmado_por_nome} em {formatDateTime(pag.pagamento_confirmado_em)}
                        </div>
                      )}
                      {pag.pagamento_baixado_em && (
                        <div className="text-green-600">
                          Baixado por {pag.pagamento_baixado_por_nome} em {formatDateTime(pag.pagamento_baixado_em)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      <div className={`font-semibold ${pag.is_convidado ? "text-gray-400" : "text-green-600"}`}>
                        R$ {(pag.valor || 0).toFixed(2).replace(".", ",")}
                      </div>
                      {!pag.is_convidado && getStatusBadge(pag.status_pagamento)}
                    </div>
                    
                    {canEdit && pag.status_pagamento === "pendente" && !pag.is_convidado && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={() => handleConfirmarPagamento(pag.pessoa_id)}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Confirmar
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
